"use client";

import { m as motion } from "framer-motion";
import { Image } from "@/components/ui/image";
import { fadeInUp, defaultViewport, defaultTransition } from "@/lib/motion";

interface FounderProfileProps {
  name: string;
  role: string;
  bio: (
    | string
    | { type: "image"; src: string; alt: string; width: number; height: number }
  )[];
  imageSrc: string;
  linkedInUrl: string;
  index: number;
}

const FounderProfile: React.FC<FounderProfileProps> = ({
  name,
  role,
  bio,
  imageSrc,
  linkedInUrl,
  index,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={defaultViewport}
    transition={{ ...defaultTransition, delay: index * 0.1 }}
    className="glass-card overflow-hidden hover-lift"
  >
    <div className="flex flex-col md:flex-row">
      <div className="md:w-1/3 p-8 flex flex-col items-center md:items-start text-center md:text-left">
        <Image
          src={imageSrc}
          alt={name}
          width={300}
          height={300}
          className="rounded-full mx-auto md:mx-0 border-4 border-primary/30 shadow-lg w-48 h-48 object-cover"
        />
        <h2 className="text-2xl font-bold mt-4 mb-2">{name}</h2>
        <h3 className="text-lg text-muted-foreground mb-4">{role}</h3>
        <a
          href={linkedInUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary-foreground inline-flex items-center bg-primary/10 hover:bg-primary px-4 py-2 rounded-full transition-all duration-300"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="mr-2"
          >
            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
          </svg>
          LinkedIn
        </a>
      </div>
      <div className="md:w-2/3 p-8 bg-card/50 rounded-r-xl">
        {bio.map((item, i) =>
          typeof item === "string" ? (
            <p key={i} className="text-foreground/80 mb-4 text-left leading-relaxed">
              {item}
            </p>
          ) : (
            <div key={i}>
              <Image
                src={item.src}
                alt={item.alt}
                width={item.width}
                height={item.height}
                className="mb-4 w-full rounded-lg shadow-md"
              />
            </div>
          )
        )}
      </div>
    </div>
  </motion.div>
);

export const AboutUsSection = () => {
  const founders = [
    {
      name: "Neil Morelli, PhD",
      role: "Instructor",
      bio: [
        "Hi, I'm Neil. My passion for the intersection of technology and talent management began during my doctoral studies in Industrial-Organizational (I-O) Psychology. I had the opportunity to publish one of the first empirical research articles on the differences between mobile and non-mobile delivered employment tests.",
        "Since then, I've experienced how technology amplifies both good and bad human capital practices. Whether companies mandate their use or employees adopt them organically, I've worked hard to understand how technology impacts HR.",
        "During my tenure as the Chief I-O Psychologist at Codility (a pre-hire assessment platform for technical skills), I've had a front-row seat to generative AI's impact on the software engineering field—the new skills AI requires, the changes to who and how companies hire engineers and wider effects on the work itself.",
        "Since ChatGPT's launch in late 2022, I've counseled dozens of current and potential customers on the impact of AI on their technical jobs. I've written several articles on the topic for Forbes, Dice, and TechCrunch. I am also working on two book chapters on AI for HR and IO psychology.",
        "Now, I want to bring what I'm learning to help other HR teams and organizations adapt to AI's mainstream arrival.",
        "Why?",
        "Because I know that AI is quickly becoming the new computing paradigm, much like cloud services, mobile, and internet-based services before it. Companies (and HR teams) that don't learn to adapt to this new reality will be left behind, wasting their workforce's potential.",
        "I also know that AI provides one of the best opportunities for HR leaders to play more strategic roles in their organizations and finally get more evidence-based talent practices in the hands of more frontline and executive leaders.",
        "AI is a tool. In the right hands, it can be a powerful force for good in the lives of millions of workers, benefiting their employers as well. I want to help you be a part of this change and direct it to be a positive outcome for you and your organization.",
      ],
      imageSrc: "/images/neil.webp",
      linkedInUrl: "https://www.linkedin.com/in/neilmorelli/",
    },
    {
      name: "Jonathan Flower",
      role: "AI Engineer, Instructor",
      bio: [
        "Hi, I'm Jonathan. I've always been fascinated by why some software is intuitive and enjoyable to use, while other programs seem designed to make life miserable. I often found myself mentally redesigning solutions, wondering why they weren't created that way in the first place.",
        "This fascination led me to study countless stories of failed software solutions. What I discovered is that most issues aren't technical—they stem from various barriers between what customers truly need and what's actually created. It might seem odd to mention this here, but I believe it illustrates not only the challenges in software development but also in creating AI solutions that genuinely meet end users' needs.",
        {
          type: "image" as const,
          src: "/images/software-dev.webp",
          alt: "coding illustration",
          width: 400,
          height: 300,
        },
        "If you've used many AI tools recently, this illustration likely resonates. The number of failed or underwhelming AI apps is staggering. AI is unlike any other innovation, making it challenging to find the right fit between the product and our actual needs.",
        "I welcome this fresh challenge after over a decade of delivering enterprise software solutions that my clients often praised for being user-friendly, fast, and intuitive.",
        "There's an immense opportunity right now to discover how AI can make people's jobs easier, faster, higher quality, and more enjoyable.",
        "I aim to equip HR leaders with the tools they need to accelerate the adoption of the most impactful and sustainable AI solutions and skills in the world.",
      ],
      imageSrc: "/images/jonathan-square.webp",
      linkedInUrl: "https://www.linkedin.com/in/jonathan-flower/",
    },
    {
      name: "Alexia Saphos",
      role: "HR Strategist & Talent Development Leader",
      bio: [
        "Alexia is an HR strategist focused on designing frameworks that connect people strategy to business growth.",
        "She has led transformative initiatives in organizational design, competency development, and performance management, and brings a practical perspective on how AI can be applied to make HR more agile and impactful.",
        "Her approach combines innovation with real-world application, helping leaders see how technology can enhance talent strategies while keeping people at the center.",
      ],
      imageSrc: "/images/1683813597077.jpeg",
      linkedInUrl: "https://www.linkedin.com/in/alexia-saphos",
    },
    {
      name: "Lindsay Hansen",
      role: "Workforce Strategy & AI Ethics Leader",
      bio: [
        "Lindsay designs people systems that sit at the intersection of AI, ethics, and workforce strategy, bringing an ethics-centered approach to organizational transformation.",
        "She has extensive experience in learning and development at Riviera Partners, where she served as Director of Learning and Development, and specializes in talent tech architecture that balances innovation with ethical considerations.",
        "Her unique perspective on AI and people systems makes her an ideal facilitator who can help leaders navigate the intersection of technology, ethics, and human capital.",
      ],
      imageSrc: "/images/lindsay-hansen.png",
      linkedInUrl: "https://www.linkedin.com/in/lthansen4",
    },
  ];

  return (
    <section className="w-full relative overflow-hidden">
      {/* Hero */}
      <div className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-mesh opacity-30 pointer-events-none" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl pointer-events-none" />

        <div className="section-inner relative z-10">
          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={defaultViewport}
            transition={defaultTransition}
            className="flex flex-col items-center text-center"
          >
            <span className="inline-block px-4 py-1.5 rounded-full glass text-sm font-medium text-primary mb-6">
              The People Behind the Program
            </span>
            <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl xl:text-6xl/none mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))]">
              Meet Your Instructors
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mt-4">
              Researchers, builders, and practitioners who've spent their careers at the intersection of people and technology.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Founder Cards */}
      <div className="section-inner pb-20 space-y-8">
        {founders.map((founder, index) => (
          <FounderProfile key={founder.name} {...founder} index={index} />
        ))}
      </div>
    </section>
  );
};
